import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { faPlus } from '@fortawesome/pro-light-svg-icons/faPlus';
import { TypedMemo } from '../../../../common/types';
import { SMLButton } from '../../../../common/views/SMLButton/SMLButton';
import { StudioActionError } from '../StudioActionError';
import { useStudioActionFeedback } from '../useStudioActionFeedback';
import { useGetReelsQuery } from '../../../../../redux/api/fymAPI';
import { Panel } from '../../../components/primitives/Panel';
import { PromoteContainer } from '../../PromoteContainer';
import { SerializedReel } from '../../types';
import { reelProgressMessages } from './reelProgressMessages';
import { reelReviewMessages } from './reelReviewMessages';
import { useReelReview } from './useReelReview';
import { ReelPreview } from './ReelPreview';
import { ReelSummary } from './ReelSummary';
import { ReelReadyActions } from './ReelReadyActions';
import { ReelApprovedNotice } from './ReelApprovedNotice';
import { ReelRejectedNotice } from './ReelRejectedNotice';
import { ReelApprovedToast } from './ReelApprovedToast';
import { ReelConflictAlert } from './ReelConflictAlert';
import { ReelRefusedNotice } from './ReelRefusedNotice';
import { ReelHistoryStrip } from './ReelHistoryStrip';
import { ReelApproveSheet } from './ReelApproveSheet';
import { ReelRejectSheet } from './ReelRejectSheet';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the preview, the history and the sheets */
import styles from './ReelReview.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ReelReviewPageProps {
  accountId: number;
  /** A ready, approved or rejected reel. */
  reel: SerializedReel;
  releaseTitle?: string;
  crumbs: string[];
  refetch: () => void;
  /** "New reel": the form with the values of this reel. */
  onNewReel: () => void;
}

/**
 * The preview of a finished reel (design c1, c2, e3, e4) with the history strip of its release.
 * Approve and Reject show only for a ready reel and open sheets d and e.
 */
const ReelReviewPageComponent: React.FC<ReelReviewPageProps> = (props) => {
  const { accountId, reel, releaseTitle, crumbs, refetch, onNewReel } = props;
  const intl = useIntl();
  const review = useReelReview({ accountId, reel, refetch });
  const makeAnotherFeedback = useStudioActionFeedback('make another AI reel', review.makeAnotherError);
  const { catalogItemUuid } = reel;
  const history = useGetReelsQuery(
    { accountId, catalogItemUuid: catalogItemUuid ?? undefined },
    { skip: catalogItemUuid === null }
  );
  const childUuid = useMemo(
    () => history.data?.reels.find((one) => one.parentReelUuid === reel.uuid)?.uuid,
    [history.data?.reels, reel.uuid]
  );

  let title = reelProgressMessages.titleReady;
  if (reel.status === 'approved') {
    title = reelReviewMessages.titleApproved;
  } else if (reel.status === 'rejected') {
    title = reelReviewMessages.titleRejected;
  }
  const isReady = reel.status === 'ready';

  return (
    <PromoteContainer
      title={intl.formatMessage(title)}
      subtitle={isReady ? intl.formatMessage(reelReviewMessages.subtitleReady) : undefined}
      crumbs={crumbs}
      actions={
        <SMLButton outline omitCap leftIcon={faPlus} onClick={onNewReel}>
          {intl.formatMessage(reelReviewMessages.newReel)}
        </SMLButton>
      }
    >
      <div className="d-flex flex-column gap-3">
        {review.approvedCount !== null && <ReelApprovedToast count={review.approvedCount} onHide={review.hideToast} />}
        {review.conflict !== null && <ReelConflictAlert code={review.conflict} />}
        {review.refusal !== null && (
          <ReelRefusedNotice
            code={review.refusal.code}
            resetsAt={review.refusal.resetsAt}
            onClose={review.closeRefusal}
          />
        )}
        <Panel className={styles.preview}>
          <ReelPreview reel={reel} releaseTitle={releaseTitle} />
          <div className={styles.side}>
            {reel.summary !== null && (
              <ReelSummary summary={reel.summary} voiceId={reel.voiceId} canEditCaption={isReady} />
            )}
            <div className={styles.hair} />
            {isReady && (
              <ReelReadyActions
                onApprove={() => review.setSheet('approve')}
                onReject={() => review.setSheet('reject')}
              />
            )}
            {reel.status === 'approved' && <ReelApprovedNotice reel={reel} />}
            {reel.status === 'rejected' && (
              <ReelRejectedNotice
                reel={reel}
                childUuid={childUuid}
                isMaking={review.isMaking}
                onMakeAnother={() => review.makeAnother().catch(console.error)}
                onOpenChild={review.openReel}
              />
            )}
            {/* A failed "Make another" shows next to its button. */}
            <StudioActionError feedback={makeAnotherFeedback} />
          </div>
        </Panel>
        {catalogItemUuid !== null && (
          <ReelHistoryStrip
            accountId={accountId}
            catalogItemUuid={catalogItemUuid}
            currentUuid={reel.uuid}
            releaseTitle={releaseTitle}
          />
        )}
      </div>
      {isReady && (
        <>
          <ReelApproveSheet
            isOpen={review.sheet === 'approve'}
            accountId={accountId}
            reel={reel}
            releaseTitle={releaseTitle}
            onClose={review.closeSheet}
            onApproved={review.onApproved}
            onConflict={review.onConflict}
          />
          <ReelRejectSheet
            isOpen={review.sheet === 'reject'}
            accountId={accountId}
            reel={reel}
            releaseTitle={releaseTitle}
            onClose={review.closeSheet}
            onChild={review.onChild}
            onRefused={review.onRefused}
            onConflict={review.onConflict}
          />
        </>
      )}
    </PromoteContainer>
  );
};

export const ReelReviewPage = TypedMemo(ReelReviewPageComponent);
